import React from 'react';

/**
 * Skeleton loader props.
 */
export interface SkeletonLoaderProps {
  /**
   * Width of the skeleton (CSS value)
   */
  width?: string | number;

  /**
   * Height of the skeleton (CSS value)
   */
  height?: string | number;

  /**
   * Border radius (CSS value)
   * Default: '0.375rem' (rounded-md)
   */
  borderRadius?: string | number;

  /**
   * Number of skeleton lines to render
   * Default: 1
   */
  count?: number;

  /**
   * Spacing between multiple skeletons (CSS value)
   * Default: '0.5rem'
   */
  spacing?: string | number;

  /**
   * Custom className
   */
  className?: string;

  /**
   * Animation style
   * - 'pulse': Pulsing opacity animation
   * - 'none': No animation
   * Default: 'pulse'
   */
  animation?: 'pulse' | 'none';

  /**
   * Variant shape
   * - 'rect': Rectangle
   * - 'circle': Circle
   * - 'text': Text line with reduced height
   * Default: 'rect'
   */
  variant?: 'rect' | 'circle' | 'text';
}

/**
 * Skeleton loader component for loading states.
 *
 * Provides a placeholder while content is loading, improving perceived performance.
 *
 * @example
 * ```typescript
 * // Simple skeleton
 * <SkeletonLoader width="100%" height="20px" />
 *
 * // Multiple lines
 * <SkeletonLoader count={3} height="16px" />
 *
 * // Circle avatar
 * <SkeletonLoader variant="circle" width="48px" height="48px" />
 *
 * // Text with no animation
 * <SkeletonLoader variant="text" animation="none" count={5} />
 * ```
 */
export function SkeletonLoader({
  width = '100%',
  height = '1rem',
  borderRadius = '0.375rem',
  count = 1,
  spacing = '0.5rem',
  className = '',
  animation = 'pulse',
  variant = 'rect',
}: SkeletonLoaderProps) {
  // Normalize CSS values
  const cssWidth = typeof width === 'number' ? `${width}px` : width;
  const cssHeight = typeof height === 'number' ? `${height}px` : height;
  const cssBorderRadius =
    typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius;
  const cssSpacing = typeof spacing === 'number' ? `${spacing}px` : spacing;

  // Determine animation class
  const animationClass = animation === 'pulse' ? 'animate-pulse' : '';

  // Determine shape
  let shape: string;
  if (variant === 'circle') {
    shape = '50%';
  } else if (variant === 'text') {
    shape = '0.25rem';
  } else {
    shape = cssBorderRadius;
  }
  const adjustedHeight = variant === 'text' ? '0.75em' : cssHeight;

  // Render single skeleton
  const renderSkeleton = (index: number) => (
    <div
      key={index}
      className={`bg-base-300 ${animationClass} ${className}`}
      style={{
        width: cssWidth,
        height: adjustedHeight,
        borderRadius: shape,
        marginBottom: index < count - 1 ? cssSpacing : undefined,
      }}
    />
  );

  // Render multiple skeletons
  if (count > 1) {
    return (
      <div className='space-y-0'>
        {Array.from({ length: count }, (_, i) => renderSkeleton(i))}
      </div>
    );
  }

  return renderSkeleton(0);
}

/**
 * Skeleton for a card component.
 *
 * @example
 * ```typescript
 * <SkeletonCard />
 * ```
 */
export function SkeletonCard({
  hasImage = true,
  lines = 3,
  className = '',
}: {
  hasImage?: boolean;
  lines?: number;
  className?: string;
}) {
  return (
    <div
      className={`rounded-lg border border-base-300 bg-base-100 p-4 ${className}`}
    >
      {hasImage && <SkeletonLoader height='200px' className='mb-4' />}
      <SkeletonLoader width='60%' height='24px' className='mb-3' />
      <SkeletonLoader count={lines} height='16px' spacing='0.5rem' />
    </div>
  );
}

/**
 * Skeleton for a list item.
 *
 * @example
 * ```typescript
 * <SkeletonListItem />
 * <SkeletonListItem hasAvatar={false} />
 * ```
 */
export function SkeletonListItem({
  hasAvatar = true,
  lines = 2,
  className = '',
}: {
  hasAvatar?: boolean;
  lines?: number;
  className?: string;
}) {
  return (
    <div className={`flex items-start gap-3 ${className}`}>
      {hasAvatar && (
        <SkeletonLoader
          variant='circle'
          width='40px'
          height='40px'
          className='flex-shrink-0'
        />
      )}
      <div className='flex-1 min-w-0'>
        <SkeletonLoader width='40%' height='18px' className='mb-2' />
        <SkeletonLoader count={lines} height='14px' variant='text' />
      </div>
    </div>
  );
}

/**
 * Skeleton for a table row.
 *
 * @example
 * ```typescript
 * <table>
 *   <tbody>
 *     <SkeletonTableRow columns={4} />
 *     <SkeletonTableRow columns={4} />
 *   </tbody>
 * </table>
 * ```
 */
export function SkeletonTableRow({
  columns = 3,
  className = '',
}: {
  columns?: number;
  className?: string;
}) {
  return (
    <tr className={className}>
      {Array.from({ length: columns }, (_, i) => (
        <td key={i} className='p-3'>
          <SkeletonLoader height='16px' />
        </td>
      ))}
    </tr>
  );
}

/**
 * Skeleton for a timeline event.
 *
 * Matches the structure of TimelineEvent component.
 *
 * @example
 * ```typescript
 * <SkeletonTimelineEvent />
 * ```
 */
export function SkeletonTimelineEvent({
  className = '',
}: {
  className?: string;
}) {
  return (
    <div className={`flex gap-3 ${className}`}>
      {/* Icon */}
      <div className='flex-shrink-0'>
        <SkeletonLoader variant='circle' width='32px' height='32px' />
      </div>

      {/* Content */}
      <div className='flex-1 min-w-0'>
        <div className='flex items-start justify-between gap-2'>
          <div className='flex-1 min-w-0'>
            <SkeletonLoader width='70%' height='18px' className='mb-2' />
            <SkeletonLoader count={2} height='14px' variant='text' />
          </div>
          <SkeletonLoader
            width='60px'
            height='12px'
            className='flex-shrink-0'
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for a full timeline list with date grouping.
 *
 * @example
 * ```typescript
 * <SkeletonTimelineList />
 * ```
 */
export function SkeletonTimelineList({
  groups = 2,
  eventsPerGroup = 3,
  className = '',
}: {
  groups?: number;
  eventsPerGroup?: number;
  className?: string;
}) {
  return (
    <div className={`space-y-6 ${className}`}>
      {Array.from({ length: groups }, (_, groupIndex) => (
        <div key={groupIndex} className='space-y-3'>
          {/* Date Header */}
          <div className='sticky top-0 z-10 bg-base-100/95 backdrop-blur-sm py-2'>
            <SkeletonLoader width='100px' height='12px' />
          </div>

          {/* Events */}
          <div className='space-y-4 relative'>
            {/* Timeline line */}
            <div className='absolute left-4 top-0 bottom-0 w-px bg-base-300' />

            {Array.from({ length: eventsPerGroup }, (_, eventIndex) => (
              <div key={eventIndex} className='relative'>
                <SkeletonTimelineEvent />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
